import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { isEqual } from 'lodash'

import { RadioButton } from 'primereact/radiobutton'

import { COLOR_OPTIONS_HELP } from '@aytm/app/stats/page/sidebar/components/lib/helps'
import { COLOR_THEMES, DEFAULT_PALETTE } from '@aytm/app/stats/page/sidebar/color-options/lib/constants'

import { wrappedPostFetch } from '@aytm/app/stats/page/sidebar/color-options/lib/func'
import { normalizedColors, normalizedPalette } from '@aytm/app/stats/page/sidebar/color-options/lib/palette'

import SidebarPopup from '@aytm/app/stats/page/sidebar/components/SidebarPopup'
import SidebarPopupWarning from '@aytm/app/stats/page/sidebar/components/SidebarPopupWarning'

import ColorThemes from '@aytm/app/stats/page/sidebar/color-options/ColorThemes'
import ColorLab from '@aytm/app/stats/page/sidebar/color-options/ColorLab'

import style from '@aytm/app/stats/page/sidebar/color-options/styles/ColorOptionsPanel.module.scss'
import PageGlobals from '@aytm/app/stats/lib/page/page-globals'
import { pressable } from '@aytm/app/stats/components/lib/pressable'

const PALETTES_URL = `${PageGlobals.AYTM_DOMAIN}/surveys/${PageGlobals.SURVEY_ID}/stat/palettes`
const MAX_CUSTOM_PALETTES = 7 // FR-CLR-UI-32
const NEW_PALETTE_ID = -1

// FR-CLR-01..08, FR-CLR-UI-01..04, FR-CLR-API-01..09
// Orchestrates: Default Palettes (ColorThemes), Custom Colors list + Add new,
// Color Lab editor (ColorLab), Orphan section. Owns palette CRUD and draft state.
const ColorOptionsPanel = ({ container, model, onModeChange, onParamsChange, onPalettesChange }) => {
  const { mode, colorTheme, palette, isAdmin, isSurveyOwner } = model

  const disabled = 'charts' != model.mode

  const [palettes, setPalettes] = useState(window.customPalettes || [])
  const [status, setStatus] = useState({}) // { remove: {id: 'confirm'|'remove'}, update: {id: 'update'} }
  const [draft, setDraft] = useState(null)
  const [draftId, setDraftId] = useState(null) // NEW_PALETTE_ID=-1 for new, server id for edit

  const componentRef = useRef(null)

  const [root, setRoot] = useState(null)

  const setComponentRef = useCallback(node => {
    componentRef.current = node
    setRoot(node?.parentNode ?? null)
  }, [])

  // Resolve active selection → team palette id (or null when not a custom match).
  const activePaletteId = useMemo(() => {
    if (null === palette) return null
    const found = palettes.find(item => isEqual(item.data, palette))
    return found ? found.id : null
  }, [palettes, palette])

  // FR-CLR-UI-04, FR-CLR-UI-37: palette set but no matching record → orphan row.
  const orphanPalette = useMemo(
    () => (null !== palette && null === activePaletteId ? palette : null),
    [palette, activePaletteId]
  )

  const paletteListAvailable = 0 < palettes.length || isAdmin || isSurveyOwner
  const editAvailable = (isAdmin || isSurveyOwner) && !disabled
  const addAvailable = palettes.length < MAX_CUSTOM_PALETTES && null === draft && editAvailable

  // FR-CLR-UI-48: save blocked against any default palette OR any existing custom palette.
  const saveAvailable = useMemo(() => {
    if (null === draft || !editAvailable) return false
    const dupDefault = COLOR_THEMES.some(theme =>
      isEqual(normalizedColors(draft.colors), normalizedColors(theme.slice(0, 7)))
    )
    const dupCustom = palettes.some(item => isEqual(normalizedPalette(draft), normalizedPalette(item.data)))
    return !dupDefault && !dupCustom
  }, [palettes, draft, editAvailable])

  // Only one id tracked per status type at a time; matches existing UX (one in-flight spinner).
  const setTypedStatus = useCallback((type, id, value) => {
    setStatus(prev => {
      const next = { ...prev, [type]: {} }
      if (null !== value) next[type][id] = value
      return next
    })
  }, [])

  const isRemoving = id => 'remove' === status.remove?.[id]
  const isConfirming = id => 'confirm' === status.remove?.[id]
  const isUpdating = id => 'update' === status.update?.[id]

  const onSelect = useCallback(
    (nextColorTheme, nextPalette) => onParamsChange({ color_theme: nextColorTheme, palette: nextPalette }, false),
    [onParamsChange]
  )

  const syncServerList = useCallback(
    list => {
      if (list) {
        window.customPalettes = list
        setPalettes(list)
      }
      onPalettesChange()
    },
    [onPalettesChange]
  )

  const closeDraft = () => {
    setDraft(null)
    setDraftId(null)
  }

  const onPaletteAdd = seed => {
    setDraft(seed || DEFAULT_PALETTE)
    setDraftId(NEW_PALETTE_ID)
  }
  const onPaletteEdit = record => {
    setDraft(record.data)
    setDraftId(record.id)
  }
  const onPaletteChange = nextColors => {
    setDraft(prev => ({ ...prev, colors: nextColors }))
  }
  const onPaletteCancel = () => closeDraft()

  const createPalette = useCallback(() => {
    setTypedStatus('update', draftId, 'update')
    wrappedPostFetch(PALETTES_URL, { data: draft })
      .then(data => {
        setTypedStatus('update', draftId, null)
        syncServerList(data.palettes)
        if ('OK' === data.status) closeDraft()
      })
      .catch(() => {
        setTypedStatus('update', draftId, null)
        syncServerList()
      })
  }, [draft, draftId, syncServerList, setTypedStatus])

  const updatePalette = useCallback(() => {
    setTypedStatus('update', draftId, 'update')
    wrappedPostFetch(`${PALETTES_URL}/${draftId}/update`, { data: draft })
      .then(data => {
        setTypedStatus('update', draftId, null)
        if ('OK' === data.status) {
          if (draftId === activePaletteId) onSelect(null, draft)
          syncServerList(data.palettes)
          closeDraft()
        } else {
          syncServerList(data.palettes)
        }
      })
      .catch(() => {
        setTypedStatus('update', draftId, null)
        syncServerList()
      })
  }, [draft, draftId, activePaletteId, onSelect, syncServerList, setTypedStatus])

  const removePalette = useCallback(
    targetId => {
      setTypedStatus('remove', targetId, 'remove')
      wrappedPostFetch(`${PALETTES_URL}/${targetId}/remove`)
        .then(data => {
          setTypedStatus('remove', targetId, null)
          syncServerList(data.palettes)
          if ('OK' === data.status && targetId === draftId) closeDraft()
        })
        .catch(() => {
          setTypedStatus('remove', targetId, null)
          syncServerList()
        })
    },
    [draftId, syncServerList, setTypedStatus]
  )

  const onPaletteSave = () => (0 < draftId ? updatePalette() : createPalette())

  // Close editor if capability turns off or the edited palette disappears from the library.
  useEffect(() => {
    const draftLost = null !== draftId && 0 < draftId && !palettes.some(item => draftId === item.id)
    if (disabled || draftLost) closeDraft()
  }, [disabled, palettes, draftId])

  return (
    <>
      <SidebarPopupWarning isSpecial={'charts' != mode} disabled={disabled} onModeChange={onModeChange} />

      <div ref={setComponentRef} className={style.component}>
        <div className={style['component-title']}>
          Color Options
          <SidebarPopup id="color" root={root} help={COLOR_OPTIONS_HELP} width="300px" />
        </div>

        <ColorThemes
          colorTheme={colorTheme}
          palettes={palettes}
          isAdmin={isAdmin}
          isSurveyOwner={isSurveyOwner}
          disabled={disabled}
          onSelect={onSelect}
          onPaletteAdd={onPaletteAdd}
        />

        {paletteListAvailable ? (
          <div className={style.section}>
            <div className={style['section-title']}>Custom Colors</div>

            {palettes.map((record, index) => (
              <Fragment key={`palette-${record.id}`}>
                <div className={classNames(style['palettes-item'], style['palettes-item-custom'])}>
                  {editAvailable ? (
                    isRemoving(record.id) ? (
                      <span className={style['palettes-item-spin']}>
                        <i className="icon-aytmfd-refresh-v2 aytmfd-spin" />
                      </span>
                    ) : (
                      <span
                        data-testid={`stat-color-options-palette-remove-${record.id}`}
                        className={style['palettes-item-delete']}
                        aria-label="Delete palette"
                        {...pressable(() => setTypedStatus('remove', record.id, 'confirm'))}
                      >
                        <i className="icon-aytmfd-x-v2" />
                      </span>
                    )
                  ) : null}
                  <RadioButton
                    data-testid={`stat-color-options-palette-select-${record.id}`}
                    className="p-radiobutton-sm"
                    aria-label={`Custom palette ${index + 1}`}
                    checked={activePaletteId === record.id}
                    disabled={disabled}
                    onChange={disabled ? null : () => onSelect(null, record.data)}
                  />
                  <div
                    className={classNames(style['palettes-item-colors'], { 'p-disabled': disabled })}
                    onClick={disabled ? null : () => onSelect(null, record.data)}
                  >
                    {record.data.colors.map((color, index) => (
                      <span
                        key={`palette-${record.id}-color-${index}`}
                        className={style['palettes-item-colors-item']}
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>
                  {editAvailable && !isRemoving(record.id) ? (
                    <span
                      data-testid={`stat-color-options-palette-edit-${record.id}`}
                      className={style['palettes-item-edit']}
                      {...pressable(() => onPaletteEdit(record))}
                    >
                      edit
                    </span>
                  ) : null}
                </div>

                {isConfirming(record.id) ? (
                  <div className={style['palettes-item-confirm']}>
                    <span
                      data-testid={`stat-color-options-palette-remove-yes-${record.id}`}
                      className={style['palettes-item-confirm-yes']}
                      {...pressable(() => removePalette(record.id))}
                    >
                      delete forever?
                    </span>
                    <span
                      data-testid={`stat-color-options-palette-remove-no-${record.id}`}
                      className={style['palettes-item-confirm-no']}
                      {...pressable(() => setTypedStatus('remove', record.id, null))}
                    >
                      nevermind
                    </span>
                  </div>
                ) : null}
              </Fragment>
            ))}

            {addAvailable && (
              <div className={style['palettes-add']}>
                <span
                  data-testid="stat-color-options-palette-add"
                  className={style['palettes-add-button']}
                  {...pressable(() => onPaletteAdd(null))}
                >
                  <i className="pi pi-plus" /> Add new
                </span>
              </div>
            )}
          </div>
        ) : null}

        {null !== draft && (
          <div className={style.section}>
            <div className={style['section-title']}>Color Lab</div>
            <div className={style['palettes-picker']}>
              <span
                data-testid="stat-color-options-palette-cancel"
                className={style['palettes-picker-cancel']}
                aria-label="Cancel palette changes"
                {...pressable(onPaletteCancel)}
              >
                <i className="icon-aytmfd-x-v2" />
              </span>
              <ColorLab container={container} palette={draft} onChange={onPaletteChange}>
                {isUpdating(draftId) ? (
                  <span className={style['palettes-picker-spin']}>
                    <i className="icon-aytmfd-refresh-v2 aytmfd-spin" />
                  </span>
                ) : (
                  <span
                    data-testid="stat-color-options-palette-save"
                    className={`${style['palettes-picker-save']}${
                      saveAvailable ? '' : ' ' + style['palettes-picker-save-disabled']
                    }`}
                    {...pressable(saveAvailable ? onPaletteSave : null)}
                  >
                    {0 < draftId ? 'update' : 'save'}
                  </span>
                )}
              </ColorLab>
            </div>
          </div>
        )}

        {null !== orphanPalette && (
          <div className={style.section}>
            <div className={style['section-title']}>Orphan Palette</div>
            <div className={classNames(style['palettes-item'], style['palettes-item-custom'])}>
              <RadioButton
                data-testid="stat-color-options-palette-orphan-select"
                className="p-radiobutton-sm"
                aria-label="Orphan palette"
                checked
                disabled={disabled}
              />
              <div className={classNames(style['palettes-item-colors'], { 'p-disabled': disabled })}>
                {orphanPalette.colors.map((color, index) => (
                  <span
                    key={`orphan-color-${index}`}
                    className={style['palettes-item-colors-item']}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
              {editAvailable && palettes.length < MAX_CUSTOM_PALETTES ? (
                <span
                  data-testid="stat-color-options-palette-copy"
                  className={style['palettes-item-copy']}
                  {...pressable(() => onPaletteAdd(orphanPalette))}
                >
                  copy
                </span>
              ) : null}
            </div>
          </div>
        )}
      </div>
    </>
  )
}

ColorOptionsPanel.propTypes = {
  container: PropTypes.any,
  model: PropTypes.object,
  onModeChange: PropTypes.func,
  onParamsChange: PropTypes.func,
  onPalettesChange: PropTypes.func
}

ColorOptionsPanel.displayName = 'ColorOptionsPanel'

export default ColorOptionsPanel
